import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FlagIcon } from "@heroicons/react/24/solid";
import { PlagiarismAnalysisViewModel, PlagiarismAnnotationViewModel } from "./scoreReportApiSlice";

export interface Props {
	className?: string;
	analysis: PlagiarismAnalysisViewModel;
}

export default function PlagiarismAnalysis(props: Props) {
	const annotationLength = props.analysis.annotations.length;
	const allAnnotations = props.analysis.annotations;
	const shortenedAnnotations = props.analysis.annotations.slice(0, 3);

	const [seeAll, setSeeAll] = useState(false);
	const [annotations, setAnnotations] = useState<PlagiarismAnnotationViewModel[]>(
		annotationLength > 3 ? shortenedAnnotations : allAnnotations
	);

	const evaluationMessage =
		annotationLength == 0
			? "No sentences were flagged for plagiarism."
			: annotationLength == 1
				? "One sentence was flagged for plagiarism."
				: `${annotationLength} sentences were flagged for plagiarism.`;

	let flagClassName = "h-14 w-14 ";
	if (props.analysis.score >= 60) {
		flagClassName += "fill-red-600";
	} else if (props.analysis.score > 30) {
		flagClassName += "fill-orange-600";
	} else if (props.analysis.score > 15) {
		flagClassName += "fill-yellow-400";
	} else {
		flagClassName += "fill-green-600";
	}

	useEffect(() => {
		setAnnotations(
			seeAll ? props.analysis.annotations : props.analysis.annotations.slice(0, 3)
		);
	}, [seeAll]);

	return (
		<>
			<h1>Plagiarism Analysis</h1>
			<div className="flex mt-8">
				<div className="w-1/4 flex flex-col">
					<div className="flex flex-row">
						<div className="w-1/2 flex justify-center">
							<FlagIcon className={flagClassName} />
						</div>
						<div className="w-1/2 flex flex-col items-center">
							<p className="text-3xl">{props.analysis.score}%</p>
							<span className="text-center">Plagiarism Detected</span>
						</div>
					</div>
					<p className="mt-6">
						Plagiarism scores range from 0% to 100%. 0% indicates no plagiarism, while
						100% indicates extreme plagiarism.
					</p>
				</div>
				<div className="w-3/4 pl-8">
					<div>
						<h2 className="mb-2">Evaluation</h2>
						<p>{evaluationMessage}</p>
					</div>

					{annotationLength != 0 && (
						<div>
							<table className="table-auto tbl tbl-data mt-4 mb-2">
								<thead>
									<tr>
										<th>Source</th>
										<th>Sentence</th>
										<th className="min-w-32">Probability</th>
									</tr>
								</thead>
								<tbody>
									{annotations?.map((item) => {
										return (
											<tr key={item.startIndex}>
												<td>
													<Link
														to={item.url}
														target="_blank"
														rel="noopener noreferrer"
														id="footer-about-us"
														className="pr-1 no-underline">
														Link
													</Link>
												</td>
												<td>{item.snippet}</td>
												<td>{item.score}%</td>
											</tr>
										);
									})}
									{annotationLength > 3 && (
										<tr>
											<td className="!border-b-0"></td>
											<td className="!border-b-0"></td>
											<td className="!border-b-0">
												<a
													className="font-medium text-black cursor-pointer "
													onClick={() => setSeeAll((v) => !v)}>
													{seeAll ? "Show Less" : "Show All"}
												</a>
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
